<script setup lang="ts">
/**
 * «Недавняя активность» — макет 17:765, правая колонка хаба.
 *
 * Фраза собирается здесь, а не в сервисе: тот отдаёт факт (что произошло, с
 * какой задачей, кто сделал), а перевод живёт в словаре — иначе лента на
 * английской локали осталась бы русской.
 *
 * Лента стоит сбоку и держит свою высоту (DEV-1590). Раньше она шла под сеткой
 * во всю ширину и росла вниз, унося за нижний край виджет «Начало работы»,
 * который стоял под ней. События сгруппированы по дням: «12 минут назад» рядом
 * с «2 часа назад» не отвечает на вопрос «что было вчера».
 */
import { GitMerge, MessageSquare, MoveRight, Plus, Rocket } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { kindKey, type ActivityEvent } from '../activity'

const props = defineProps<{
  events: ActivityEvent[]
  /** Ждём следующую порцию — кнопка «Показать ещё» занята */
  loading?: boolean
  /** Есть ли что догружать: на последней порции кнопку не показываем */
  hasMore?: boolean
}>()

const emit = defineEmits<{ more: [] }>()

const { t, locale } = useI18n()

const ICONS = {
  created: Plus,
  moved: MoveRight,
  comment: MessageSquare,
  merged: GitMerge,
  devops: Rocket,
  other: Plus,
}

/** Календарный день события в поясе читателя — по нему и группируем */
const dayOf = (iso: string) => {
  const at = new Date(iso)

  return Number.isNaN(at.getTime()) ? '' : at.toDateString()
}

const dayLabel = (iso: string) => {
  const today = new Date().toDateString()
  const yesterday = new Date(Date.now() - 86_400_000).toDateString()
  const day = dayOf(iso)

  if (day === today) return t('hub.activity.today')
  if (day === yesterday) return t('hub.activity.yesterday')

  return new Date(iso).toLocaleDateString(locale.value, { day: 'numeric', month: 'long' })
}

/** Время события: день уже сказан заголовком группы, здесь остаются часы */
const timeOf = (iso: string) => {
  const at = new Date(iso)

  if (Number.isNaN(at.getTime())) return ''

  return at.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
}

const groups = computed(() => {
  const byDay: { key: string; label: string; rows: ReturnType<typeof toRow>[] }[] = []

  for (const event of props.events) {
    const day = dayOf(event.createdAt)
    const last = byDay[byDay.length - 1]

    if (!last || last.key !== day) {
      byDay.push({ key: day, label: dayLabel(event.createdAt), rows: [toRow(event)] })

      continue
    }

    last.rows.push(toRow(event))
  }

  return byDay
})

const toRow = (event: ActivityEvent) => {
  const key = kindKey(event.kind)

  return {
    key: `${event.issueKey}-${event.createdAt}-${key}`,
    icon: ICONS[key as keyof typeof ICONS] ?? ICONS.other,
    issueKey: event.issueKey,
    actor: event.actorName || t('hub.activity.someone'),
    // Глагол отдельно от имени: в макете он серый, а имя тёмное.
    verb: t(`hub.activity.verbs.${key}`),
    subject: event.subject || `${event.issueKey} ${event.issueTitle}`.trim(),
    when: timeOf(event.createdAt),
  }
}
</script>

<template>
  <section class="feed">
    <h2 class="feed__title">{{ t('hub.activity.title') }}</h2>

    <p v-if="!events.length" class="feed__empty">{{ t('hub.activity.empty') }}</p>

    <!-- Прокрутка внутри ленты: сколько бы событий ни догрузили, соседние
         блоки хаба остаются на своих местах. -->
    <div v-else class="feed__scroll">
      <template v-for="group in groups" :key="group.key">
        <p class="feed__day">{{ group.label }}</p>

        <RouterLink
          v-for="row in group.rows"
          :key="row.key"
          class="feed__row"
          :to="row.issueKey ? `/issue/${row.issueKey}` : '/projects'"
        >
          <component :is="row.icon" class="feed__icon" :size="14" />

          <span class="feed__body">
            <span class="feed__who">
              <span class="feed__actor">{{ row.actor }}</span>
              <span class="feed__verb">{{ row.verb }}</span>
            </span>
            <span class="feed__subject">{{ row.subject }}</span>
          </span>

          <span class="feed__when">{{ row.when }}</span>
        </RouterLink>
      </template>
    </div>

    <button
      v-if="hasMore && events.length"
      type="button"
      class="feed__more"
      :disabled="loading"
      @click="emit('more')"
    >
      {{ loading ? t('hub.activity.loading') : t('hub.activity.more') }}
    </button>
  </section>
</template>

<style scoped>
.feed {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
}

.feed__title {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.feed__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

/* Высота — примерно шесть событий: дальше лента перестаёт быть сводкой и
   становится журналом, за которым приходят не сюда. */
.feed__scroll {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  overflow-y: auto;
}

.feed__day {
  margin: 8px 0 2px;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.feed__day:first-child {
  margin-top: 0;
}

.feed__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 4px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.feed__row:hover {
  background: var(--color-bg-muted);
}

.feed__icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-muted);
}

.feed__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
}

/* Имя и действие переносятся вместе: в колонке 344 они в строку не встают. */
.feed__who {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.feed__actor {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.feed__verb {
  color: var(--color-text-muted);
}

/* Объект события обрезается: ключ и начало названия говорят, о чём речь. */
.feed__subject {
  overflow: hidden;
  color: var(--color-text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feed__when {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.feed__more {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.feed__more:hover:not(:disabled) {
  color: var(--color-primary);
}

.feed__more:disabled {
  cursor: progress;
}
</style>
